import type { AiChatMessage } from "@productlane/lib/src/ai"

type DocsProposalChanges = NonNullable<
	NonNullable<AiChatMessage["docsProposalSuggestion"]>["changes"]
>

export type DocsProposalOutcome = { finishedAt: number } & (
	| { status: "ready"; changes: DocsProposalChanges; rationale?: string }
	| { status: "skipped" }
)

interface GroupProposal {
	id: string
	kind: "CREATE" | "EDIT" | "DELETE"
	status: string
	articleId?: string | null
	proposedTitle?: string | null
	article?: { title?: string | null } | null
}

interface ProposalGroup {
	status: string
	createdAt: number
	description?: string | null
	proposals?: readonly GroupProposal[]
}

// Null rather than an empty array, so the answer only goes inline when a pill
// is actually there to sit at the end of it.
export function readyDocsProposalChanges(
	message: AiChatMessage,
): DocsProposalChanges | null {
	const suggestion = message.docsProposalSuggestion
	if (suggestion?.status !== "ready") return null
	return suggestion.changes?.length ? suggestion.changes : null
}

export function docsProposalChanges(
	proposals: readonly GroupProposal[],
): DocsProposalChanges {
	return proposals
		.filter((proposal) => proposal.status === "OPEN")
		.map((proposal) => ({
			id: proposal.id,
			kind: proposal.kind,
			title: proposal.proposedTitle ?? proposal.article?.title ?? "Untitled",
			route: proposal.articleId
				? `/edit-docs/review/${proposal.articleId}`
				: `/edit-docs/review-new/${proposal.id}`,
		}))
}

// Null while the run is still going: the group row only lands once it ends. A
// run that staged nothing closes its group empty, which is how a card that
// would otherwise wait forever learns the run is over.
export function docsProposalOutcome(
	group: ProposalGroup | null | undefined,
): DocsProposalOutcome | null {
	if (!group) return null
	const changes = docsProposalChanges(group.proposals ?? [])
	if (changes.length > 0)
		return {
			status: "ready",
			changes,
			rationale: group.description ?? undefined,
			finishedAt: group.createdAt,
		}
	return group.status === "OPEN"
		? null
		: { status: "skipped", finishedAt: group.createdAt }
}

export function applyDocsProposalOutcome(
	messages: readonly AiChatMessage[],
	messageId: string,
	outcome: DocsProposalOutcome,
): AiChatMessage[] | null {
	let didChange = false

	const next = messages.map((message) => {
		if (message.id !== messageId) return message
		const suggestion = message.docsProposalSuggestion
		if (!suggestion || suggestion.status === "error") return message
		if (outcome.status === "skipped") {
			if (suggestion.status === "skipped") return message
			didChange = true
			return {
				...message,
				docsProposalSuggestion: {
					...suggestion,
					status: "skipped" as const,
					finishedAt: outcome.finishedAt,
				},
			}
		}
		if (
			suggestion.status === "ready" &&
			suggestion.changes?.length === outcome.changes.length
		) {
			return message
		}
		didChange = true
		return {
			...message,
			docsProposalSuggestion: {
				...suggestion,
				status: "ready" as const,
				changes: outcome.changes,
				rationale: outcome.rationale,
				finishedAt: outcome.finishedAt,
			},
		}
	})

	return didChange ? next : null
}
